import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import "../../../styles/customize-components/new-table.css";

const NewTable = ({
  // Data and configuration
  data = [],
  columns = [],
  title = "Table",
  summaryLabel = "ITEMS",
  summary, // optional custom summary string

  // Pagination
  pagination = true,
  defaultPageSize = 5,
  pageSizeOptions = [5, 10, 20, 30, 40, 50, 100, 200],

  // Search and filters
  searchable = true,
  searchPlaceholder = "Search...",
  filters = [],

  // Actions
  actions = [],
  onEdit,
  onDelete,
  onView,

  // Customization
  className = "",
  emptyMessage = "No data found",
  loading = false,

  // Callbacks
  onRowClick,
  onSelectionChange,

  // Styling
  compact = false,
  striped = true,
  hoverable = true,

  // Additional features
  selectable = false,
  bulkActions = [],
  onBulkAction,

  // Custom renderers
  customCellRenderers = {},
  customHeaderRenderers = {},

  // Metadata
  totalCount,
  showTotalCount = true,

  // Custom components
  CustomEmptyState,
  CustomLoadingState,
  CustomHeader,
  CustomFooter,

  // Table specific props
  tableClassName = "",
  headerClassName = "",
  bodyClassName = "",
  rowClassName = "",
  cellClassName = "",
}) => {
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultPageSize);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Filter and search logic
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Apply search
    if (searchable && searchTerm) {
      filtered = filtered.filter((item) => {
        return columns.some((column) => {
          const value = item[column.key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        });
      });
    }

    // Apply filters
    Object.keys(selectedFilters).forEach((filterKey) => {
      const filterValue = selectedFilters[filterKey];
      if (filterValue && filterValue !== "all") {
        filtered = filtered.filter((item) => {
          const value = item[filterKey];
          return String(value) === String(filterValue);
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, selectedFilters, sortConfig, searchable, columns]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(parseInt(newLimit));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (filterKey, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleRowSelection = (rowId, checked) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, rowId]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== rowId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(currentData.map((item) => item.id || item.uid));
    } else {
      setSelectedRows([]);
    }
  };

  const handleBulkAction = (action) => {
    if (onBulkAction) {
      onBulkAction(action, selectedRows);
    }
  };

  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const handleEdit = (row) => {
    if (onEdit) {
      onEdit(row);
    } else {
      toast.info("Edit functionality not implemented");
    }
  };

  const handleDelete = (row) => {
    const handleConfirm = () => {
      if (onDelete) {
        onDelete(row);
      } else {
        toast.success(`Item deleted successfully!`);
      }
    };

    showConfirmAlert(
      handleConfirm,
      `Are you sure you want to delete this item?`,
      "Cancel",
      "Delete"
    );
  };

  const handleView = (row) => {
    if (onView) {
      onView(row);
    } else {
      toast.info("View functionality not implemented");
    }
  };

  // Render helpers
  const renderCell = (item, column) => {
    const { key, render, type = "text" } = column;
    const value = item[key];

    // Custom renderer takes priority
    if (render) {
      return render(value, item, column);
    }

    // Type-based renderers
    switch (type) {
      case "badge":
        return (
          <span
            className={`new-tb-type-badge ${
              value?.toLowerCase() || "default"
            }`}>
            {value}
          </span>
        );
      case "status":
        return (
          <span
            className={
              value === "active"
                ? "new-tb-status-badge status-active"
                : value === "inactive"
                ? "new-tb-status-badge status-inactive"
                : "new-tb-status-badge"
            }>
            {value.toUpperCase()}
          </span>
        );
      case "date":
        return value ? new Date(value).toLocaleDateString() : "-";
      case "datetime":
        return value ? new Date(value).toLocaleString() : "-";
      case "number":
        return value?.toLocaleString() || "0";
      case "currency":
        return value ? `$${parseFloat(value).toFixed(2)}` : "$0.00";
      case "percentage":
        return value ? `${parseFloat(value).toFixed(1)}%` : "0%";
      case "boolean":
        return value ? "✅" : "❌";
      case "array":
        return Array.isArray(value) ? value.join(", ") : value;
      case "object":
        return value ? JSON.stringify(value) : "-";
      default:
        return value || "-";
    }
  };

  const renderHeader = (column) => {
    const { key, title, sortable = false, renderHeader } = column;

    // if (renderHeader) {
    //   return renderHeader(column);
    // }

    // if (sortable) {
    //   return (
    //     <button
    //       className={`ris-sortable-header ${
    //         sortConfig.key === key ? "active" : ""
    //       }`}
    //       onClick={() => handleSort(key)}>
    //       {title}
    //       {sortConfig.key === key && (
    //         <span className="ris-sort-indicator">
    //           {sortConfig.direction === "asc" ? "↑" : "↓"}
    //         </span>
    //       )}
    //     </button>
    //   );
    // }

    return title;
  };

  const renderActions = (row) => {
    if (!actions.length) return null;

    return (
      <div className="new-tb-action-buttons">
        {actions.map((action, index) => {
          const { type, label, icon, onClick, confirm, disabled } = action;

          const handleAction = () => {
            if (confirm) {
              showConfirmAlert(
                () => onClick(row),
                confirm,
                "Cancel",
                "Confirm"
              );
            } else {
              onClick(row);
            }
          };

          const isDisabled = disabled ? disabled(row) : false;

          switch (type) {
            case "edit":
              return (
                <button
                  key={index}
                  className="new-tb-btn new-tb-btn-edit"
                  onClick={() => handleEdit(row)}
                  disabled={isDisabled}
                  title={label || "Edit"}>
                  {icon || "✏️"} {label || "Edit"}
                </button>
              );
            case "delete":
              return (
                <button
                  key={index}
                  className="new-tb-btn new-tb-btn-delete"
                  onClick={() => handleDelete(row)}
                  disabled={isDisabled}
                  title={label || "Delete"}>
                  {icon || "🗑️"} {label || "Delete"}
                </button>
              );
            case "view":
              return (
                <button
                  key={index}
                  className="new-tb-btn new-tb-btn-view"
                  onClick={() => handleView(row)}
                  disabled={isDisabled}
                  title={label || "View"}>
                  {icon || "👁️"} {label || "View"}
                </button>
              );
            default:
              return (
                <button
                  key={index}
                  className="new-tb-btn new-tb-btn-custom"
                  onClick={handleAction}
                  disabled={isDisabled}
                  title={label}>
                  {icon} {label}
                </button>
              );
          }
        })}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return CustomLoadingState ? (
      <CustomLoadingState />
    ) : (
      <div className="new-tb-loading-state">
        <div className="new-tb-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className={`new-tb-table-wrapper ${className}`}>
      {/* Pagination Controls (Top) */}
      {pagination && totalPages > 1 && (
        <div
          className={`new-tb-pagination new-tb-pagination-top ${tableClassName}`}>
          <div className="new-tb-pagination-info">
            Page {currentPage} of {totalPages}
          </div>

          <div className="new-tb-pagination-controls">
            <button
              className="new-tb-btn new-tb-btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}>
              ← Previous
            </button>

            <div className="new-tb-page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`new-tb-page-btn ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(page)}>
                    {page}
                  </button>
                )
              )}
            </div>

            <button
              className="new-tb-btn new-tb-btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}>
              Next →
            </button>
          </div>

          {/* Items per page */}
          <div className="new-tb-items-per-page">
            <label htmlFor="itemsPerPage">Show:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}>
              {pageSizeOptions.map((limit) => (
                <option key={limit} value={limit}>
                  {limit}
                </option>
              ))}
            </select>
            <span>per page</span>
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className={`new-tb-table-container ${tableClassName}`}>
        <table
          className={`new-tb-data-table ${compact ? "compact" : ""} ${
            striped ? "striped" : ""
          } ${hoverable ? "hoverable" : ""}`}>
          <thead className={headerClassName}>
            <tr>
              {/* Selection checkbox */}
              {selectable && (
                <th className="new-tb-checkbox-cell">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === currentData.length &&
                      currentData.length > 0
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}

              {/* Column headers */}
              {columns.map((column, index) => (
                <th key={index} className={column.headerClassName || ""}>
                  {renderHeader(column)}
                </th>
              ))}

              {/* Actions column */}
              {actions.length > 0 && (
                <th className="new-tb-actions-header">Actions</th>
              )}
            </tr>
          </thead>

          <tbody className={bodyClassName}>
            {currentData.map((row, rowIndex) => (
              <tr
                key={row.id || row.uid || rowIndex}
                className={`${rowClassName} ${onRowClick ? "clickable" : ""}`}
                onClick={() => handleRowClick(row)}>
                {/* Selection checkbox */}
                {selectable && (
                  <td className="new-tb-checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id || row.uid)}
                      onChange={(e) =>
                        handleRowSelection(row.id || row.uid, e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                )}

                {/* Data cells */}
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`${cellClassName} ${
                      column.cellClassName || ""
                    }`}>
                    {renderCell(row, column)}
                  </td>
                ))}

                {/* Actions cell */}
                {actions.length > 0 && (
                  <td className="new-tb-actions-cell">{renderActions(row)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty state */}
        {currentData.length === 0 &&
          (CustomEmptyState ? (
            <CustomEmptyState />
          ) : (
            <div className="new-tb-empty-state">
              <p>{emptyMessage}</p>
            </div>
          ))}
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="new-tb-pagination">
          <div className="new-tb-pagination-info">
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, filteredData.length)} of {filteredData.length}{" "}
            results
          </div>

          <div className="new-tb-pagination-controls">
            <button
              className="new-tb-btn new-tb-btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}>
              ← Previous
            </button>

            <div className="new-tb-page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`new-tb-page-btn ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => handlePageChange(page)}>
                    {page}
                  </button>
                )
              )}
            </div>

            <button
              className="new-tb-btn new-tb-btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}>
              Next →
            </button>
          </div>

          {/* Items per page */}
          <div className="new-tb-items-per-page">
            <label htmlFor="itemsPerPage">Show:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}>
              {pageSizeOptions.map((limit) => (
                <option key={limit} value={limit}>
                  {limit}
                </option>
              ))}
            </select>
            <span>per page</span>
          </div>
        </div>
      )}

      {/* Custom Footer */}
      {CustomFooter && <CustomFooter />}
    </div>
  );
};

export default NewTable;
